/**
 * One role's chapter on `/learn/access` (13 Oct): when it is your turn, the
 * real screens with numbered callouts, common mistakes, then 2–4 check
 * questions with instant feedback. The role is complete when every question
 * is answered right (retries allowed); the first-try score is kept.
 *
 * The content is English for now (`lang="en"`); the chrome is translated.
 */

import { CircleCheck, CircleX, Icon, Lightbulb, TriangleAlert } from "@norican/icons";
import { Alert, Badge, Button, Card, cn, Typography } from "@norican/react";
import { Fragment, useState } from "react";

import { useT } from "@/i18n";

import { useCompleteTraining } from "./api";
import {
  answer,
  type Chapter,
  firstTryScore,
  isComplete,
  parseMarkup,
  questionStatus,
  type QuizState,
} from "./lib/training";

/** Light markup → text with bold runs and ①… callouts. */
export function Markup({ text }: { text: string }) {
  return (
    <>
      {parseMarkup(text).map((part, i) =>
        part.bold ? (
          <strong key={i}>{part.text}</strong>
        ) : part.callout ? (
          <span key={i} className="text-danger-fg font-bold">
            {part.text}
          </span>
        ) : (
          <Fragment key={i}>{part.text}</Fragment>
        ),
      )}
    </>
  );
}

export interface ChapterStatus {
  completed: boolean;
  outOfDate: boolean;
}

export interface ChapterViewProps {
  role: string;
  title: string;
  chapter: Chapter;
  status: ChapterStatus;
  version: string;
}

function Quiz({ role, chapter, version, status }: Omit<ChapterViewProps, "title">) {
  const { t } = useT();
  const [state, setState] = useState<QuizState>({});
  const [saved, setSaved] = useState(false);
  const complete = useCompleteTraining();
  const quiz = chapter.quiz;
  const allRight = isComplete(state, quiz);

  const pick = (index: number, option: number) => {
    const next = answer(state, index, option, quiz);
    setState(next);
    if (!saved && isComplete(next, quiz)) {
      setSaved(true);
      complete.mutate(
        { role: role as never, version, score: firstTryScore(next, quiz), total: quiz.length },
        { onError: () => setSaved(false) },
      );
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 lg:grid-cols-2 lg:gap-x-8">
        {quiz.map((question, index) => {
          const status_ = questionStatus(state, index, quiz);
          const picks = state[index] ?? [];
          return (
            <fieldset key={question.q} className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium" lang="en">
                {index + 1}. {question.q}
              </legend>
              <div className="flex flex-col gap-1.5">
                {question.o.map((option, o) => {
                  const chosen = picks.includes(o);
                  const right = chosen && o === question.a;
                  const wrong = chosen && o !== question.a;
                  return (
                    <button
                      key={option}
                      type="button"
                      lang="en"
                      disabled={status_ === "right"}
                      aria-pressed={chosen}
                      onClick={() => pick(index, o)}
                      className={cn(
                        "flex items-start gap-2 rounded-md border px-3 py-2 text-start text-sm",
                        right && "border-success-border bg-success-subtle",
                        wrong && "border-danger-border bg-danger-subtle line-through",
                        !chosen && "border-border hover:bg-surface-hover",
                        status_ === "right" && !chosen && "opacity-60",
                      )}
                    >
                      {right ? <Icon icon={CircleCheck} size="sm" tone="success" /> : null}
                      {wrong ? <Icon icon={CircleX} size="sm" tone="danger" /> : null}
                      {option}
                    </button>
                  );
                })}
              </div>
              <div aria-live="polite">
                {status_ === "right" ? (
                  <Typography as="p" variant="body-sm" className="text-success-fg">
                    {t("accessTraining.quiz.right")} <span lang="en">{question.why}</span>
                  </Typography>
                ) : status_ === "wrong" ? (
                  <Typography as="p" variant="body-sm" className="text-danger-fg">
                    {t("accessTraining.quiz.wrong")}
                  </Typography>
                ) : null}
              </div>
            </fieldset>
          );
        })}
      </div>
      {allRight ? (
        complete.isError ? (
          <Alert tone="danger">
            <div className="flex flex-wrap items-center gap-2">
              {t("accessTraining.quiz.saveFailed")}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSaved(true);
                  complete.mutate(
                    {
                      role: role as never,
                      version,
                      score: firstTryScore(state, quiz),
                      total: quiz.length,
                    },
                    { onError: () => setSaved(false) },
                  );
                }}
              >
                {t("button.retry")}
              </Button>
            </div>
          </Alert>
        ) : (
          <Alert tone="success">
            {t("accessTraining.quiz.allRight", {
              score: firstTryScore(state, quiz),
              total: quiz.length,
            })}
          </Alert>
        )
      ) : status.completed ? null : (
        <Typography as="p" variant="caption" tone="muted">
          {t("accessTraining.quiz.progress", {
            done: quiz.filter((_, i) => questionStatus(state, i, quiz) === "right").length,
            total: quiz.length,
          })}
        </Typography>
      )}
    </div>
  );
}

export function ChapterView({ role, title, chapter, status, version }: ChapterViewProps) {
  const { t } = useT();
  const [retake, setRetake] = useState(false);
  let n = 0;
  return (
    <section
      id={`role-${role}`}
      aria-labelledby={`role-${role}-title`}
      className="scroll-mt-[calc(var(--app-header-height,4rem)+var(--learn-bar-height,3rem)+1rem)]"
    >
      <Card variant="outline" padding="lg" className="flex flex-col gap-5">
        <header className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Typography as="h2" variant="heading-4" id={`role-${role}-title`}>
              {title}
            </Typography>
            {status.completed ? (
              <Badge tone="success" variant="subtle" size="sm">
                {t("accessTraining.chapter.completed")}
              </Badge>
            ) : status.outOfDate ? (
              <Badge tone="warning" variant="subtle" size="sm">
                {t("accessTraining.chapter.outOfDate")}
              </Badge>
            ) : (
              <Badge tone="neutral" variant="subtle" size="sm">
                {t("accessTraining.chapter.notDone")}
              </Badge>
            )}
          </div>
          <Typography as="p" variant="body-sm" tone="muted" lang="en">
            {chapter.title}
          </Typography>
        </header>

        <div className="bg-bg-subtle flex flex-col gap-1 rounded-md p-3">
          <Typography as="h3" variant="heading-6">
            {t("accessTraining.chapter.when")}
          </Typography>
          <Typography as="p" variant="body-sm" lang="en">
            {chapter.when} <Markup text={chapter.intro} />
          </Typography>
          {chapter.note ? (
            <Typography as="p" variant="body-sm" tone="muted" lang="en">
              {chapter.note}
            </Typography>
          ) : null}
        </div>

        <div className="flex flex-col gap-4">
          <Typography as="h3" variant="heading-5">
            {t("accessTraining.chapter.steps")}
          </Typography>
          {chapter.sections.map((section) => (
            <div key={section.title} className="flex flex-col gap-3">
              <Typography as="h4" variant="heading-6" lang="en">
                {section.title}
              </Typography>
              <ol className="flex flex-col gap-4">
                {section.steps.map((step) => {
                  n += 1;
                  return (
                    <li
                      key={`${n}-${step.text}`}
                      className={cn(
                        "grid gap-3",
                        // Wide screens (12 Oct): the screenshot left, the step beside it.
                        step.img ? "lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:gap-6" : null,
                      )}
                    >
                      <div className="flex gap-3 lg:order-2">
                        <span className="bg-fg text-fg-on-inverse flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-bold">
                          {n}
                        </span>
                        <Typography
                          as="p"
                          variant="body-sm"
                          lang="en"
                          className="max-w-prose min-w-0 pt-1 text-pretty"
                        >
                          <Markup text={step.text} />
                        </Typography>
                      </div>
                      {step.img ? (
                        // Opens full size: the tall forms are scaled to fit the screen.
                        <a
                          href={`/training/access/${step.img}`}
                          target="_blank"
                          rel="noreferrer"
                          className="self-start max-lg:ms-10 lg:order-1"
                        >
                          <img
                            src={`/training/access/${step.img}`}
                            alt={section.title}
                            loading="lazy"
                            className="border-border w-full max-w-3xl rounded-md border shadow-sm lg:max-h-[85vh] lg:w-auto lg:max-w-full"
                          />
                        </a>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="border-warning-border flex flex-col gap-2 rounded-md border p-3">
            <Typography as="h3" variant="heading-6" className="flex items-center gap-2">
              <Icon icon={TriangleAlert} size="sm" tone="warning" />
              {t("accessTraining.chapter.mistakes")}
            </Typography>
            <ul className="list-disc ps-5 text-sm" lang="en">
              {chapter.mistakes.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
          <div className="border-success-border flex flex-col gap-2 rounded-md border p-3">
            <Typography as="h3" variant="heading-6" className="flex items-center gap-2">
              <Icon icon={Lightbulb} size="sm" tone="success" />
              {t("accessTraining.chapter.done")}
            </Typography>
            <Typography as="p" variant="body-sm" lang="en">
              {chapter.done}
            </Typography>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Typography as="h3" variant="heading-5">
            {t("accessTraining.chapter.check")}
          </Typography>
          {status.completed && !retake ? (
            <div className="flex flex-wrap items-center gap-3">
              <Typography as="p" variant="body-sm" className="text-success-fg">
                {t("accessTraining.quiz.alreadyDone")}
              </Typography>
              <Button size="sm" variant="outline" onClick={() => setRetake(true)}>
                {t("accessTraining.quiz.retake")}
              </Button>
            </div>
          ) : (
            <Quiz role={role} chapter={chapter} version={version} status={status} />
          )}
        </div>
      </Card>
    </section>
  );
}
